import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import api, { getErr } from "../lib/api";
import { NeonButton } from "./Neon";

export const AIChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hey! I'm the Richie Rich assistant. Ask me anything about competitions, entries, or the Members Club." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chat", { message: msg, session_id: sessionId });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "bot", text: data.reply || "..." }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "bot", text: getErr(e, "Sorry, I couldn't reach the assistant.") }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed z-[70] bottom-24 right-4 md:bottom-24 md:right-6 h-14 w-14 rounded-full bg-[var(--neon-cyan)] text-black flex items-center justify-center shadow-[0_0_24px_rgba(0,229,255,.6)] hover:brightness-110 transition"
        aria-label="Open assistant"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div data-testid="ai-chat-widget" className="fixed z-[70] bottom-40 right-4 md:bottom-44 md:right-6 w-[90vw] max-w-sm h-[60vh] max-h-[520px] flex flex-col rounded-2xl overflow-hidden bg-[var(--surface)] border border-[rgba(0,229,255,.4)] shadow-[0_0_40px_rgba(0,229,255,.25)]">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-black">
            <Bot className="h-5 w-5 text-[var(--neon-green)]" />
            <span className="font-display uppercase tracking-widest text-sm text-white">Richie Assistant</span>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[var(--neon-pink)] text-black" : "bg-black border border-white/10 text-[var(--foreground)]"}`}>{m.text}</div>
              </div>
            ))}
            {sending && <div className="text-xs text-[var(--text-muted)] px-1">Assistant is typing…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-white/10 flex items-center gap-2 bg-black">
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask a question…"
              className="flex-1 bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)]"
            />
            <NeonButton data-testid="ai-chat-send" variant="cyan" className="px-3 py-2" onClick={send} loading={sending}>
              <Send className="h-4 w-4" />
            </NeonButton>
          </div>
        </div>
      )}
    </>
  );
};
